import { useFeaturedCourse } from "@/hooks/use-courses";

export function AuthLayout({ children }: { children: React.ReactNode }) {
  const { data: featured } = useFeaturedCourse();
  return (
    <div className="flex min-h-screen" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col"
        style={{ background: "#0f1944" }}
      >
        {/* Subtle grid pattern */}
        <div className="absolute inset-0" style={{ opacity: 0.06 }}>
          <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="auth-grid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#auth-grid)" />
          </svg>
        </div>

        {/* Glow orbs */}
        <div
          className="absolute rounded-full"
          style={{
            width: 400,
            height: 400,
            background: "radial-gradient(circle, rgba(67,52,220,0.35) 0%, transparent 70%)",
            top: "-80px",
            left: "-80px",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 300,
            height: 300,
            background: "radial-gradient(circle, rgba(122,201,170,0.2) 0%, transparent 70%)",
            bottom: "80px",
            right: "-60px",
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3 px-12 pt-10">
          <img
            src="https://cdn.prod.website-files.com/635aa29ce589b950d718bcf0/6994cc2f6b492812cb31cffa_nymbl_fox.svg"
            alt="Nymbl Lyrn"
            style={{ height: 36, width: "auto" }}
          />
          <span
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontSize: 22,
              fontWeight: 800,
              color: "#ffffff",
              letterSpacing: "-0.02em",
            }}
          >
            Nymbl Lyrn
          </span>
        </div>

        {/* Main content */}
        <div className="relative z-10 flex flex-col justify-center flex-1 px-12 xl:px-16 pb-16">
          <h1
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontSize: 36,
              fontWeight: 800,
              color: "#ffffff",
              lineHeight: 1.2,
              letterSpacing: "-0.025em",
              marginBottom: 12,
            }}
          >
            Your SA training,
            <br />
            all in one place.
          </h1>

          <p
            style={{
              fontSize: 14,
              color: "rgba(255,255,255,0.55)",
              lineHeight: 1.7,
              maxWidth: 340,
              marginBottom: 32,
            }}
          >
            Nymbl's growing library of courses built to sharpen your AI fluency, client delivery
            skills, and technical depth.
          </p>

          {/* Featured course card */}
          <div
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 12,
              padding: "18px 20px",
              maxWidth: 360,
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="#fbbf24"
                aria-hidden="true"
                style={{ flexShrink: 0 }}
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#fbbf24",
                }}
              >
                Featured Course
              </span>
            </div>

            <p
              style={{
                fontFamily: "'Raleway', sans-serif",
                fontSize: 16,
                fontWeight: 800,
                color: "#ffffff",
                margin: "0 0 6px",
              }}
            >
              {featured?.title ?? "SA AI Foundations"}
            </p>
            <p
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.6,
                margin: "0 0 14px",
              }}
            >
              {featured?.description ??
                "Core AI vocabulary, architectures, and delivery methodology every Nymbl SA needs."}
            </p>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.65)",
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 50,
                  padding: "3px 10px",
                }}
              >
                {featured ? `${featured.totalModules} Modules` : "7 Modules"}
              </span>
            </div>
          </div>

          {/* More coming teaser */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                display: "flex",
                gap: -4,
              }}
            >
              {["#4334dc", "#7c3aed", "#0ea5e9"].map((color, i) => (
                <div
                  key={color}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: color,
                    border: "2px solid #0f1944",
                    marginLeft: i === 0 ? 0 : -6,
                  }}
                />
              ))}
            </div>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
              More courses coming soon
            </span>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex w-full lg:w-1/2 flex-col" style={{ background: "#f7f9ff" }}>
        {/* Mobile logo */}
        <div className="flex items-center gap-3 p-5 lg:hidden" style={{ background: "#0f1944" }}>
          <img
            src="https://cdn.prod.website-files.com/635aa29ce589b950d718bcf0/6994cc2f6b492812cb31cffa_nymbl_fox.svg"
            alt="Nymbl Lyrn"
            style={{ height: 28, width: "auto" }}
          />
          <span
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontSize: 18,
              fontWeight: 800,
              color: "#ffffff",
            }}
          >
            Nymbl Lyrn
          </span>
        </div>

        {/* Centered form */}
        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">{children}</div>
        </div>

        <p className="text-center pb-6" style={{ fontSize: 11, color: "#608099" }}>
          © {new Date().getFullYear()} Nymbl LLC · Do Not Distribute
        </p>
      </div>
    </div>
  );
}
